// Real Feature Flags Service
// This service provides dynamic feature flag management and checking

import { supabase } from './supabase'
import {
  FeatureFlag,
  TenantFeatureFlag,
  FeatureFlagUsage,
  CreateFeatureFlagRequest,
  UpdateFeatureFlagRequest,
  CreateTenantFeatureFlagRequest,
  UpdateTenantFeatureFlagRequest,
  FeatureFlagFilters,
  FeatureFlagStats,
  FeatureFlagUsageStats,
  FeatureFlagName,
  FeatureFlagsApiResponse,
  FeatureFlagApiResponse,
  TenantFeatureFlagApiResponse,
  FeatureFlagStatsApiResponse,
  FeatureFlagUsageStatsApiResponse
} from '../types/featureFlags.types'

export class FeatureFlagsService {
  // ============================================================================
  // FEATURE FLAG MANAGEMENT (Super Admin Only)
  // ============================================================================

  /**
   * Get all feature flags
   */
  static async getFeatureFlags(filters?: FeatureFlagFilters): Promise<FeatureFlag[]> {
    try {
      // First check if the feature_flags table exists
      const { data: tableCheck, error: tableError } = await supabase
        .from('feature_flags')
        .select('id')
        .limit(1)

      if (tableError) {
        console.warn('feature_flags table not found, returning empty array')
        return []
      }

      let query = supabase
        .from('feature_flags')
        .select('*')
        .order('category', { ascending: true })
        .order('name', { ascending: true })

      if (filters?.category) {
        query = query.eq('category', filters.category)
      }

      if (filters?.isGlobal !== undefined) {
        query = query.eq('is_global', filters.isGlobal)
      }

      if (filters?.search) {
        query = query.or(`name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`)
      }

      const { data, error } = await query

      if (error) {
        console.error('Error fetching feature flags:', error)
        throw new Error(error.message)
      }

      return data || []
    } catch (error) {
      console.error('Error in getFeatureFlags:', error)
      throw error
      return []
    }
  }

  /**
   * Get a single feature flag by ID
   */
  static async getFeatureFlag(id: string): Promise<FeatureFlag | null> {
    try {
      // First check if the feature_flags table exists
      const { data: tableCheck, error: tableError } = await supabase
        .from('feature_flags')
        .select('id')
        .limit(1)

      if (tableError) {
        console.warn('feature_flags table not found, returning null')
        return null
      }

      const { data, error } = await supabase
        .from('feature_flags')
        .select('*')
        .eq('id', id)
        .single()

      if (error) {
        console.error('Error fetching feature flag:', error)
        throw new Error(error.message)
      }

      return data
    } catch (error) {
      console.error('Error in getFeatureFlag:', error)
      throw error
    }
  }

  /**
   * Create a new feature flag
   */
  static async createFeatureFlag(request: CreateFeatureFlagRequest): Promise<FeatureFlag> {
    try {
      // First check if the feature_flags table exists
      const { data: tableCheck, error: tableError } = await supabase
        .from('feature_flags')
        .select('id')
        .limit(1)

      if (tableError) {
        console.warn('feature_flags table not found, cannot create feature flag')
        throw new Error('Feature flags system not initialized')
      }

      const { data, error } = await supabase
        .from('feature_flags')
        .insert({
          name: request.name,
          description: request.description,
          category: request.category,
          is_global: request.isGlobal,
          default_enabled: request.defaultEnabled
        })
        .select()
        .single()

      if (error) {
        console.error('Error creating feature flag:', error)
        throw new Error(error.message)
      }

      return data
    } catch (error) {
      console.error('Error in createFeatureFlag:', error)
      throw error
    }
  }

  /**
   * Update an existing feature flag
   */
  static async updateFeatureFlag(id: string, request: UpdateFeatureFlagRequest): Promise<FeatureFlag> {
    try {
      // First check if the feature_flags table exists
      const { data: tableCheck, error: tableError } = await supabase
        .from('feature_flags')
        .select('id')
        .limit(1)

      if (tableError) {
        console.warn('feature_flags table not found, cannot update feature flag')
        throw new Error('Feature flags system not initialized')
      }

      const updateData: any = {}
      
      if (request.name !== undefined) updateData.name = request.name
      if (request.description !== undefined) updateData.description = request.description
      if (request.category !== undefined) updateData.category = request.category
      if (request.isGlobal !== undefined) updateData.is_global = request.isGlobal
      if (request.defaultEnabled !== undefined) updateData.default_enabled = request.defaultEnabled

      const { data, error } = await supabase
        .from('feature_flags')
        .update(updateData)
        .eq('id', id)
        .select()
        .single()

      if (error) {
        console.error('Error updating feature flag:', error)
        throw new Error(error.message)
      }

      return data
    } catch (error) {
      console.error('Error in updateFeatureFlag:', error)
      throw error
    }
  }

  /**
   * Delete a feature flag
   */
  static async deleteFeatureFlag(id: string): Promise<void> {
    try {
      // First check if the feature_flags table exists
      const { data: tableCheck, error: tableError } = await supabase
        .from('feature_flags')
        .select('id')
        .limit(1)

      if (tableError) {
        console.warn('feature_flags table not found, cannot delete feature flag')
        throw new Error('Feature flags system not initialized')
      }

      const { error } = await supabase
        .from('feature_flags')
        .delete()
        .eq('id', id)

      if (error) {
        console.error('Error deleting feature flag:', error)
        throw new Error(error.message)
      }
    } catch (error) {
      console.error('Error in deleteFeatureFlag:', error)
      throw error
    }
  }

  // ============================================================================
  // TENANT FEATURE FLAG MANAGEMENT
  // ============================================================================

  /**
   * Get tenant feature flags for a specific tenant
   */
  static async getTenantFeatureFlags(tenantId: string): Promise<TenantFeatureFlag[]> {
    try {
      // First check if the tenant_feature_flags table exists
      const { data: tableCheck, error: tableError } = await supabase
        .from('tenant_feature_flags')
        .select('id')
        .limit(1)

      if (tableError) {
        console.warn('tenant_feature_flags table not found, returning empty array')
        return []
      }

      const { data, error } = await supabase
        .from('tenant_feature_flags')
        .select(`
          *,
          feature_flag:feature_flags(*)
        `)
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching tenant feature flags:', error)
        throw new Error(error.message)
      }

      return data || []
    } catch (error) {
      console.error('Error in getTenantFeatureFlags:', error)
      throw error
    }
  }

  /**
   * Get all tenant feature flags across all tenants
   */
  static async getAllTenantFeatureFlags(): Promise<TenantFeatureFlag[]> {
    try {
      // First check if the tenant_feature_flags table exists
      const { data: tableCheck, error: tableError } = await supabase
        .from('tenant_feature_flags')
        .select('id')
        .limit(1)

      if (tableError) {
        console.warn('tenant_feature_flags table not found, returning empty array')
        return []
      }

      // Try to get tenant information if available
      let query = supabase
        .from('tenant_feature_flags')
        .select(`
          *,
          feature_flag:feature_flags(*)
        `)
        .order('tenant_id', { ascending: true })
        .order('created_at', { ascending: false })

      const { data, error } = await query

      if (error) {
        console.error('Error fetching all tenant feature flags:', error)
        throw new Error(error.message)
      }

      return data || []
    } catch (error) {
      console.error('Error in getAllTenantFeatureFlags:', error)
      throw error
    }
  }

  /**
   * Create or update a tenant feature flag
   */
  static async upsertTenantFeatureFlag(request: CreateTenantFeatureFlagRequest): Promise<TenantFeatureFlag> {
    try {
      // First check if the tenant_feature_flags table exists
      const { data: tableCheck, error: tableError } = await supabase
        .from('tenant_feature_flags')
        .select('id')
        .limit(1)

      if (tableError) {
        console.warn('tenant_feature_flags table not found, cannot upsert tenant feature flag')
        throw new Error('Feature flags system not initialized')
      }

      // Use upsert with conflict resolution on the unique constraint
      const { data, error } = await supabase
        .from('tenant_feature_flags')
        .upsert({
          tenant_id: request.tenantId,
          feature_flag_id: request.featureFlagId,
          is_enabled: request.isEnabled,
          rollout_percentage: request.rolloutPercentage || 100,
          enabled_for_roles: request.enabledForRoles || ['super_admin', 'admin', 'manager', 'member'],
          start_date: request.startDate,
          end_date: request.endDate,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'tenant_id,feature_flag_id'
        })
        .select(`
          *,
          feature_flag:feature_flags(*)
        `)
        .single()

      if (error) {
        console.error('Error upserting tenant feature flag:', error)
        throw new Error(error.message)
      }

      return data
    } catch (error) {
      console.error('Error in upsertTenantFeatureFlag:', error)
      throw error
    }
  }

  /**
   * Update a tenant feature flag
   */
  static async updateTenantFeatureFlag(id: string, request: UpdateTenantFeatureFlagRequest): Promise<TenantFeatureFlag> {
    try {
      // First check if the tenant_feature_flags table exists
      const { data: tableCheck, error: tableError } = await supabase
        .from('tenant_feature_flags')
        .select('id')
        .limit(1)

      if (tableError) {
        console.warn('tenant_feature_flags table not found, cannot update tenant feature flag')
        throw new Error('Feature flags system not initialized')
      }

      const updateData: any = {}
      
      if (request.isEnabled !== undefined) updateData.is_enabled = request.isEnabled
      if (request.rolloutPercentage !== undefined) updateData.rollout_percentage = request.rolloutPercentage
      if (request.enabledForRoles !== undefined) updateData.enabled_for_roles = request.enabledForRoles
      if (request.startDate !== undefined) updateData.start_date = request.startDate
      if (request.endDate !== undefined) updateData.end_date = request.endDate

      const { data, error } = await supabase
        .from('tenant_feature_flags')
        .update(updateData)
        .eq('id', id)
        .select(`
          *,
          feature_flag:feature_flags(*)
        `)
        .single()

      if (error) {
        console.error('Error updating tenant feature flag:', error)
        throw new Error(error.message)
      }

      return data
    } catch (error) {
      console.error('Error in updateTenantFeatureFlag:', error)
      throw error
    }
  }

  /**
   * Delete a tenant feature flag
   */
  static async deleteTenantFeatureFlag(id: string): Promise<void> {
    try {
      // First check if the tenant_feature_flags table exists
      const { data: tableCheck, error: tableError } = await supabase
        .from('tenant_feature_flags')
        .select('id')
        .limit(1)

      if (tableError) {
        console.warn('tenant_feature_flags table not found, cannot delete tenant feature flag')
        throw new Error('Feature flags system not initialized')
      }

      const { error } = await supabase
        .from('tenant_feature_flags')
        .delete()
        .eq('id', id)

      if (error) {
        console.error('Error deleting tenant feature flag:', error)
        throw new Error(error.message)
      }
    } catch (error) {
      console.error('Error in deleteTenantFeatureFlag:', error)
      throw error
    }
  }

  // ============================================================================
  // FEATURE FLAG CHECKING
  // ============================================================================

  /**
   * Check if a feature is enabled for a specific user
   */
  static async isFeatureEnabled(
    featureName: FeatureFlagName,
    tenantId: string,
    userId: string,
    userRole?: string
  ): Promise<boolean> {
    try {
      // First check if the feature_flags table exists
      const { data: tableCheck, error: tableError } = await supabase
        .from('feature_flags')
        .select('id')
        .limit(1)

      if (tableError) {
        console.warn('feature_flags table not found, defaulting to enabled')
        return true
      }

      // Use the database function for feature flag checking
      const { data, error } = await supabase
        .rpc('is_feature_enabled_for_user', {
          p_feature_name: featureName,
          p_tenant_id: tenantId,
          p_user_id: userId,
          p_user_role: userRole
        })

      if (error) {
        console.error('Error checking feature flag:', error)
        // Fallback to default behavior
        return true
      }

      return data || false
    } catch (error) {
      console.error('Error in isFeatureEnabled:', error)
      // Fallback to default behavior
      return true
    }
  }

  /**
   * Check if multiple features are enabled
   */
  static async areFeaturesEnabled(
    featureNames: FeatureFlagName[],
    tenantId: string,
    userId: string,
    userRole?: string
  ): Promise<boolean> {
    try {
      // First check if the feature_flags table exists
      const { data: tableCheck, error: tableError } = await supabase
        .from('feature_flags')
        .select('id')
        .limit(1)

      if (tableError) {
        console.warn('feature_flags table not found, defaulting all features to enabled')
        return true
      }

      const results = await Promise.all(
        featureNames.map(featureName => 
          this.isFeatureEnabled(featureName, tenantId, userId, userRole)
        )
      )
      
      return results.every(result => result === true)
    } catch (error) {
      console.error('Error in areFeaturesEnabled:', error)
      return false
    }
  }

  // ============================================================================
  // FEATURE USAGE TRACKING
  // ============================================================================

  /**
   * Log feature usage
   * NOTE: Currently disabled due to database function issues. Can be re-enabled once fixed.
   */
  static async logFeatureUsage(
    tenantId: string,
    featureName: FeatureFlagName,
    userId: string,
    featureEnabled: boolean,
    context?: Record<string, any>
  ): Promise<void> {
    // Feature usage logging is temporarily disabled to prevent console errors
    // The log_feature_usage RPC function is returning 400 errors
    // This is non-critical functionality, so we'll skip it for now
    return
    
    // Original implementation (disabled):
    /*
    try {
      // First check if the feature_flag_usage table exists
      const { data: tableCheck, error: tableError } = await supabase
        .from('feature_flag_usage')
        .select('id')
        .limit(1)

      if (tableError) {
        console.warn('feature_flag_usage table not found, skipping usage logging')
        return
      }

      // Try to call the RPC function, but don't let errors break the app
      // Wrap in try-catch to prevent any errors from bubbling up
      try {
        // Ensure all parameters are strings/text as expected by the function
        const { error: rpcError } = await supabase.rpc('log_feature_usage', {
          p_tenant_id: String(tenantId || ''),
          p_feature_name: String(featureName || ''),
          p_user_id: String(userId || ''),
          p_feature_enabled: Boolean(featureEnabled),
          p_context: context || null
        })
        
        // Silently ignore errors - logging is optional and shouldn't break the app
        // Don't log errors to avoid console spam
      } catch (rpcCallError) {
        // Silently ignore - feature usage logging shouldn't break the app
        // This catch prevents any unexpected errors from bubbling up
      }
    } catch (error) {
      // Silently ignore - feature usage logging shouldn't break the app
    }
    */
  }

  /**
   * Get feature usage statistics
   */
  static async getFeatureUsageStats(
    tenantId: string,
    timeRange: 'day' | 'week' | 'month' | 'year' = 'month'
  ): Promise<FeatureFlagUsageStats> {
    try {
      // First check if the feature_flag_usage table exists
      const { data: tableCheck, error: tableError } = await supabase
        .from('feature_flag_usage')
        .select('id')
        .limit(1)

      if (tableError) {
        console.warn('feature_flag_usage table not found, returning empty stats')
        return {
          totalUsage: 0,
          enabledUsage: 0,
          disabledUsage: 0,
          byFeature: {},
          byUser: {}
        }
      }

      const { data, error } = await supabase
        .from('feature_flag_usage')
        .select(`
          *,
          feature_flag:feature_flags(name),
          user:users(name, email)
        `)
        .eq('tenant_id', tenantId)

      if (error) {
        console.error('Error fetching feature usage stats:', error)
        throw new Error(error.message)
      }

      // Process the data to create statistics
      const stats: FeatureFlagUsageStats = {
        totalUsage: 0,
        enabledUsage: 0,
        disabledUsage: 0,
        byFeature: {},
        byUser: {}
      }

      data?.forEach(usage => {
        stats.totalUsage++
        if (usage.feature_enabled) {
          stats.enabledUsage++
        } else {
          stats.disabledUsage++
        }

        // By feature
        const featureName = usage.feature_flag?.name || 'unknown'
        if (!stats.byFeature[featureName]) {
          stats.byFeature[featureName] = { total: 0, enabled: 0, disabled: 0, lastUsed: '' }
        }
        stats.byFeature[featureName].total++
        if (usage.feature_enabled) {
          stats.byFeature[featureName].enabled++
        } else {
          stats.byFeature[featureName].disabled++
        }
        if (usage.accessed_at > stats.byFeature[featureName].lastUsed) {
          stats.byFeature[featureName].lastUsed = usage.accessed_at
        }

        // By user
        const userName = usage.user?.name || usage.user?.email || 'unknown'
        if (!stats.byUser[userName]) {
          stats.byUser[userName] = { total: 0, enabled: 0, disabled: 0, lastUsed: '' }
        }
        stats.byUser[userName].total++
        if (usage.feature_enabled) {
          stats.byUser[userName].enabled++
        } else {
          stats.byUser[userName].disabled++
        }
        if (usage.accessed_at > stats.byUser[userName].lastUsed) {
          stats.byUser[userName].lastUsed = usage.accessed_at
        }
      })

      return stats
    } catch (error) {
      console.error('Error in getFeatureUsageStats:', error)
      throw error
    }
  }

  // ============================================================================
  // STATISTICS AND ANALYTICS
  // ============================================================================

  /**
   * Get feature flag statistics
   */
  static async getFeatureFlagStats(): Promise<FeatureFlagStats> {
    try {
      const [featureFlags, tenantFeatureFlags] = await Promise.all([
        this.getFeatureFlags(),
        this.getAllTenantFeatureFlags()
      ])

      const stats: FeatureFlagStats = {
        totalFlags: featureFlags.length,
        enabledFlags: 0,
        disabledFlags: 0,
        byCategory: {} as Record<string, number>,
        byTenant: {}
      }

      // Count by category
      featureFlags.forEach(flag => {
        if (!stats.byCategory[flag.category]) {
          stats.byCategory[flag.category] = 0
        }
        stats.byCategory[flag.category]++
      })

      // Count by tenant
      tenantFeatureFlags.forEach(tenantFlag => {
        const tenantName = tenantFlag.tenantId || 'Unknown'
        if (!stats.byTenant[tenantName]) {
          stats.byTenant[tenantName] = { total: 0, enabled: 0, disabled: 0 }
        }
        stats.byTenant[tenantName].total++
        if (tenantFlag.isEnabled) {
          stats.byTenant[tenantName].enabled++
          stats.enabledFlags++
        } else {
          stats.byTenant[tenantName].disabled++
          stats.disabledFlags++
        }
      })

      return stats
    } catch (error) {
      console.error('Error in getFeatureFlagStats:', error)
      throw error
    }
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Get feature flags with tenant status for a specific tenant
   */
  static async getFeatureFlagsWithTenantStatus(tenantId: string): Promise<FeatureFlag[]> {
    try {
      const [featureFlags, tenantFeatureFlags] = await Promise.all([
        this.getFeatureFlags(),
        this.getTenantFeatureFlags(tenantId)
      ])

      // Create a map of tenant feature flags for quick lookup
      const tenantFlagsMap = new Map(
        tenantFeatureFlags.map(tf => [tf.featureFlagId, tf])
      )

      // Merge the data
      return featureFlags.map(flag => ({
        ...flag,
        tenantStatus: tenantFlagsMap.get(flag.id) ? {
          isEnabled: tenantFlagsMap.get(flag.id)!.isEnabled,
          rolloutPercentage: tenantFlagsMap.get(flag.id)!.rolloutPercentage,
          enabledForRoles: tenantFlagsMap.get(flag.id)!.enabledForRoles,
          startDate: tenantFlagsMap.get(flag.id)!.startDate,
          endDate: tenantFlagsMap.get(flag.id)!.endDate
        } : undefined
      }))
    } catch (error) {
      console.error('Error in getFeatureFlagsWithTenantStatus:', error)
      // Return empty array on error instead of throwing
      return []
    }
  }

  /**
   * Bulk update tenant feature flags
   */
  static async bulkUpdateTenantFeatureFlags(
    tenantId: string,
    updates: Array<{ featureFlagId: string; isEnabled: boolean; rolloutPercentage?: number }>
  ): Promise<void> {
    try {
      // First check if the tenant_feature_flags table exists
      const { data: tableCheck, error: tableError } = await supabase
        .from('tenant_feature_flags')
        .select('id')
        .limit(1)

      if (tableError) {
        console.warn('tenant_feature_flags table not found, skipping bulk update')
        return
      }

      const promises = updates.map(update =>
        this.upsertTenantFeatureFlag({
          tenantId,
          featureFlagId: update.featureFlagId,
          isEnabled: update.isEnabled,
          rolloutPercentage: update.rolloutPercentage
        })
      )

      await Promise.all(promises)
    } catch (error) {
      console.error('Error in bulkUpdateTenantFeatureFlags:', error)
      throw error
    }
  }
}

// Export a default instance for convenience
export default FeatureFlagsService
